import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { CalcScreen } from '../../components/shell';
import { C, FONT, R, SP } from '../../theme/tokens';

type Hood = {
  type: string;
  faceVelocity: string;
  faceVelocityMPS: string;
  description: string;
  applications: string;
  features: string[];
};

const HOODS: Hood[] = [
  {
    type: 'Standard Fume Hood',
    faceVelocity: '80–120 ft/min',
    faceVelocityMPS: '0.41–0.61 m/s',
    description: 'General laboratory use for handling common chemicals and solvents.',
    applications: 'General chemistry, teaching labs, routine chemical handling.',
    features: [
      'Bypass design for consistent face velocity',
      'Sash height typically 18–24 inches',
      'Standard exhaust system',
      'Suitable for most laboratory applications',
    ],
  },
  {
    type: 'High Performance Fume Hood',
    faceVelocity: '60–100 ft/min',
    faceVelocityMPS: '0.30–0.51 m/s',
    description: 'Energy-efficient design with reduced airflow requirements.',
    applications: 'Energy-conscious facilities, LEED-certified buildings, modern labs.',
    features: [
      'Reduced face velocity requirements',
      'VAV compatible',
      'Lower energy consumption',
      'Maintains containment at lower velocities',
      'Advanced airflow management',
    ],
  },
  {
    type: 'Radioisotope Fume Hood',
    faceVelocity: '100–150 ft/min',
    faceVelocityMPS: '0.51–0.76 m/s',
    description: 'Designed for handling radioactive materials with enhanced containment.',
    applications: 'Nuclear medicine, radioisotope research, radioactive material handling.',
    features: [
      'Higher face velocity for enhanced containment',
      'Sealed construction to prevent leakage',
      'Specialized filtration systems',
      'Radiation monitoring capabilities',
      'Stainless steel construction',
    ],
  },
  {
    type: 'Perchloric Acid Fume Hood',
    faceVelocity: '150–200 ft/min',
    faceVelocityMPS: '0.76–1.02 m/s',
    description: 'Specialized hood for perchloric acid and explosive materials.',
    applications: 'Perchloric acid, explosive material preparation, high-hazard chemicals.',
    features: [
      'Highest face velocity requirements',
      'Wash-down capability for perchlorate removal',
      'Stainless steel or non-reactive materials',
      'Specialized exhaust treatment',
      'Enhanced containment design',
    ],
  },
];

export default function FumehoodTypesScreen() {
  return (
    <CalcScreen title="Fume Hood Types" subtitle="Classifications and typical face velocity ranges">
      <Text style={styles.intro}>
        Fume hoods are classified by design and application. Face velocity is the average
        speed of air moving through the face opening of the hood, typically measured in ft/min
        or m/s.
      </Text>

      <Text style={styles.sectionLabel}>Classifications</Text>
      {HOODS.map((hood) => (
        <View key={hood.type} style={styles.card}>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{hood.type}</Text>
          </View>

          <View style={styles.velocityBox}>
            <View style={styles.row}>
              <Text style={styles.velLabel}>Face Velocity</Text>
              <Text style={styles.velValue}>{hood.faceVelocity}</Text>
            </View>
            <Text style={styles.velAlt}>({hood.faceVelocityMPS})</Text>
          </View>

          <Text style={styles.description}>{hood.description}</Text>

          <Text style={styles.metaLabel}>Typical Applications</Text>
          <Text style={styles.metaText}>{hood.applications}</Text>

          <Text style={styles.metaLabel}>Key Features</Text>
          {hood.features.map((f) => (
            <Text key={f} style={styles.bullet}>• {f}</Text>
          ))}
        </View>
      ))}

      <Text style={styles.sectionLabel}>Reference Standards</Text>
      <View style={styles.card}>
        <Text style={styles.bullet}>• ASHRAE 110 — Method of Testing Performance of Laboratory Fume Hoods.</Text>
        <Text style={styles.bullet}>• ANSI/AIHA Z9.5 — Laboratory Ventilation.</Text>
        <Text style={styles.bullet}>• Face velocity measurements at multiple points.</Text>
        <Text style={styles.bullet}>• Containment testing with smoke visualization.</Text>
        <Text style={styles.bullet}>• Pressure differential verification.</Text>
      </View>
    </CalcScreen>
  );
}

const styles = StyleSheet.create({
  intro: {
    fontFamily: FONT.sans,
    fontSize: 14,
    color: C.ink2,
    lineHeight: 20,
    marginBottom: SP.md,
  },
  sectionLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: SP.sm,
    marginBottom: SP.sm,
  },
  card: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.md,
    marginBottom: SP.sm + 4,
  },
  badge: {
    alignSelf: 'flex-start',
    backgroundColor: C.panel2,
    borderWidth: 1,
    borderColor: C.edge,
    paddingHorizontal: SP.sm + 4,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: SP.sm,
  },
  badgeText: {
    fontFamily: FONT.sansBold,
    fontSize: 13,
    color: C.ink,
  },
  velocityBox: {
    backgroundColor: C.panel2,
    borderWidth: 1,
    borderColor: C.edge,
    borderRadius: R.field,
    padding: SP.sm + 4,
    marginBottom: SP.sm,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  velLabel: {
    fontFamily: FONT.sansMed,
    fontSize: 13,
    color: C.ink2,
  },
  velValue: {
    fontFamily: FONT.monoBold,
    fontSize: 13,
    color: C.ink,
  },
  velAlt: {
    fontFamily: FONT.mono,
    fontSize: 12,
    color: C.dim,
    marginTop: 2,
    textAlign: 'right',
  },
  description: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    lineHeight: 19,
    marginBottom: SP.sm,
  },
  metaLabel: {
    fontFamily: FONT.sansMed,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginTop: SP.sm,
    marginBottom: SP.xs,
  },
  metaText: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    lineHeight: 19,
  },
  bullet: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    lineHeight: 19,
    marginLeft: SP.sm,
  },
});
